import { EventEmitter, Injectable } from '@angular/core';

import { PathTest, PathTestPoint, PathTestPointSeq, TaskType, activity_status, sortByNumericWaypointOrder, PathTestParticipant, TestSeq } from '../models';
import { AppState } from '../app-state';

import { PathTestDoc, pathTestMethods, pathTestSchema } from '../schema/path-test-schema';
import { TestbedRxdbCouchCollectionService } from './testbed-rxdb/testbed-rxdb-couch-collection.service';
import { TestbedRxdbState } from './testbed-rxdb/testbed-rxdb-state';
import { AuthenticationService } from './authentication.service';
import { PathTestMapPoint } from '../models/mapping'
import { PathWaypointService } from './path-waypoint.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { first } from 'rxjs/operators';
import { AppConfig } from '../../../environments/environment';
import { TestSeqService } from './test-seq.service';
import { PointTestLogService } from './point-test-log.service';
import { scoutPathNumberToTestPathName, ScoutPointChange, ScoutTaskService } from './scout-task.service';
import { firstValueFrom } from 'rxjs';


@Injectable({ providedIn: "root" })
export class PathTestService extends TestbedRxdbCouchCollectionService<
  PathTest,
  PathTestDoc
> {
  participants: PathTestParticipant[] = AppConfig.pathTest.participants || [
    { name: "att", title: "AT&T" },
    { name: "verizon", title: "Verizon" },
    { name: "tmobile", title: "T-Mobile", alias: "tmo" },
    { name: "dish", title: "Dish" },
  ];

  deleted$ = new EventEmitter<PathTestDoc>();

  constructor(
    appState: AppState,
    authService: AuthenticationService,
    public pathWaypointService: PathWaypointService,
    public testSeqService: TestSeqService,
    public logService: PointTestLogService,
    public bsModalService: BsModalService,
    rxdbState: TestbedRxdbState
  ) {
    super("path_test", pathTestSchema, appState, rxdbState, authService);

    this.methods = pathTestMethods;
    this.syncBatchSize = 50;
    this.syncConfig = {
      push: true,
      pull: true,
      intervalMs: this.appState.settings.get(
        "rxdb.collections.path_test.intervalMs",
        0
      ),
      retry: true,
      name: "task",
      query: (c) => c.find().where("type").eq(TaskType.PATH_TEST),
    };

    this.migrationStrategies = {
      1: (pathTest) => pathTest,
      2: (pathTest) => pathTest,
      3: (pathTest) => pathTest,
    };
  }

  async handleScoutPointChange({
    scoutTask,
    scoutPath,
    scoutPoint,
    changes,
  }: ScoutPointChange) {
    if (!("disabled" in changes)) {
      return;
    }
    const scoutPtId = scoutPoint.pointId || scoutPoint.exPointId;
    const pathId = scoutPathNumberToTestPathName(
      scoutTask.buildingId,
      scoutPath.pathNumber
    );
    const title = `Cart/Testbed Test Points for ${scoutPtId} and scout path #${scoutPath.pathNumber}`;

    const plsWaitModalRef = this.bsModalService.show(ConfirmModalComponent, {
      animated: false, // IMPORTANT.  keep this turned off because if animation is not complete before calling modal.hide(), it will not be hidden
      backdrop: 'static',
      ignoreBackdropClick: true,
      class: 'modal-lg modal-edge-shadow',
      initialState: {
        noBtnText: null,
        yesBtnText: null,
        title,
        question:
          '<i class="fa fa-spinner fa-spin mr-2"></i>Please wait... searching for test point(s) with status ' +
          (scoutPoint.disabled
            ? "'NOT STARTED' or 'IN PROGRESS'"
            : "'NOT USING'") +
          `.`,
      },
    });
    let pathTests: PathTestDoc[];
    try {
      pathTests = await this.findByPointIdAndPathId(scoutPtId, pathId);
    } finally {
      plsWaitModalRef.hide();
    }
    const testPointsPerTest = pathTests.map((test) => ({
      test,
      testPoints: test.testPoints.filter(
        (pt) =>
          pt.pointId === scoutPtId &&
          (changes.disabled
            ? [activity_status.Id.NOT_STARTED, activity_status.Id.IN_PROGRESS]
            : [activity_status.Id.NOT_USING]
          ).includes(pt.statusId)
      ),
    }));
    const doTaskPointsStatusChange = async (): Promise<void> => {
      console.log("doTaskPointsStatusChange()", {
        scoutPointDisabled: scoutPoint.disabled,
      });
      const promises = await Promise.all(
        testPointsPerTest.map(({ test, testPoints }) =>
          Promise.all(
            testPoints.map((tp) =>                
              this.updateTestPoint(test, tp, (tp) => {
                tp.statusId = scoutPoint.disabled
                  ? activity_status.Id.NOT_USING
                  : tp.seqs.length > 0
                  ? activity_status.Id.IN_PROGRESS
                  : activity_status.Id.NOT_STARTED;
                return tp;
              })
            )
          )
        )
      );
      const results = await Promise.allSettled(promises);
      const failures = results.filter((result) => result.status === "rejected");
      console.error(failures);
      this.appState.publishError(
        "Error changing test point(s) status. " +
        failures.map((failure:PromiseRejectedResult) => `${failure.reason}`),
        this
      );
      return;
    };

    const candidatePts = testPointsPerTest.map((pt) => pt.testPoints).flat();
    if (candidatePts.length === 0) {
      return;
    }
    const confirmModalRef = this.bsModalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-lg modal-edge-shadow',
      initialState: {
        onYes: doTaskPointsStatusChange,
        title,
        question:
          "Change test point(s) status " +
          (scoutPoint.disabled
            ? "from 'NOT STARTED' or 'IN PROGRESS' to 'NOT USING'"
            : "from 'NOT USING' to 'NOT STARTED' or 'IN PROGRESS'") +
          ` (${candidatePts.length} test points)?`,
      },
    });
    await firstValueFrom(confirmModalRef.onHidden);
  }

  findOneByTestPointId = async (testPointId: string) => {
    return await this.rxCollection
      .findOne()
      .where({
        testPoints: {
          $elemMatch: { id: testPointId },
        },
      })
      .exec();
  };

  findByPointIdAndPathId = async (pointId: string, pathId: string) => {
    console.log("findByPointIdAndPathOrder()", {
      pointId,
      pathId,
    });
    return await this.rxCollection
      .find()
      .where({
        pathId,
        testPoints: {
          $elemMatch: { pointId },
        },
      })
      .exec();
  };

  findLatestTestPointAndSeq = (testPoints: PathTestPoint[]): [PathTestPoint|null,PathTestPointSeq|null] => {
    const latestTestPointSeqFn = ([latestPt, latestSeq], pt): [PathTestPoint|null,PathTestPointSeq|null] => {
      var ptLastestSeq: PathTestPointSeq = null;
      if (pt?.seqs?.length) {
        // find latest seq with a startTime for this test pt...
        for (var i = pt.seqs.length - 1; i >= 0 && !ptLastestSeq; i--) {
          const s = pt.seqs[i];
          ptLastestSeq = s?.startTime ? s : null;
        }
      }
      const latestTime = latestSeq?.startTime || "";
      const ptLastestSeqTime = ptLastestSeq?.startTime || "";
      return ptLastestSeqTime?.localeCompare(latestTime) > 0
        ? [pt, ptLastestSeq]
        : [latestPt, latestSeq];
    };
    const latestPtAndSeq = testPoints?.reduce(latestTestPointSeqFn, [
      null,
      null,
    ]);
    //console.log(JSON.stringify(latestPtAndSeq))
    return latestPtAndSeq;
  };

  async schedule(pathTest: any) {
    try {
      pathTest.status.scheduled = {
        timestamp: this.nowTimeStamp(),
        userId: this.appState.getUser()._id,
      };

      const pathTestDoc = await this.rxCollection.findOne(pathTest._id).exec();
      if (pathTestDoc) {
        await pathTestDoc.atomicUpdate((targetPathTest) => {
          Object.assign(targetPathTest, pathTest);
          return targetPathTest;
        });
      } else {
        throw new Error(
          `Scheduling error: Could not find path test document by ID (building=${
            pathTest?._id || pathTest?.buildingId
          }).`
        );
      }
    } catch (error) {
      this.publishErrorObj(
        error,
        `Error scheduling scout (${pathTest?._id || pathTest?.buildingId})`,
        this
      );
    }
  }

  async stopTest(
    pathTestDoc: PathTestDoc,
    testPoint: PathTestPoint,
    seq: PathTestPointSeq,
    dialerData: { callsPlaced: number; callsCompleted: number } = null
  ) {
    console.log(`stopTest(${testPoint?.pointId})`);
    console.dir(seq);
    const nowTimestamp = this.nowTimeStamp();
    const updatePointFn = (testPoint: PathTestPoint) => {
      if (!testPoint.seqs) {
        testPoint.seqs = [];
      }
      var testPointSeqIndex = testPoint.seqs?.findIndex((s) => s.id == seq.id);
      if (testPointSeqIndex == -1) {
        testPoint.seqs.push(seq);
      } else {
        testPoint.seqs[testPointSeqIndex] = seq;
      }
      if (!seq.stopTime) {
        //should have already been set by dialer-client-server while processing dialer result
        seq.stopTime = nowTimestamp;
      }
      if (dialerData) {
        seq.data.callsPlaced = dialerData.callsPlaced;
        seq.data.callsCompleted = dialerData.callsCompleted;
      }
      console.log("saving seq on testPoint " + testPoint.pointId);
      console.dir(seq);
      testPoint.statusId = activity_status.Id.COMPLETE;
    };
    return await this.updateTestPoint(pathTestDoc, testPoint, updatePointFn);
  }

  updateTestPointArrayContext(mapPoints: PathTestMapPoint[]) {
    const sortFn = (a, b) =>
      sortByNumericWaypointOrder(a.pathWaypoint, b.pathWaypoint);
    const startPts = mapPoints
      ?.sort(sortFn)
      .filter((tp) => !tp.pathWaypoint?.fromPointId);
    startPts.forEach((startPt) =>
      this.pathWaypointService.updateWaypointContext(startPt, mapPoints)
    );
  }

  async updateTestPoint(
    pathTestDoc: PathTestDoc,
    srcTestPoint: PathTestPoint,
    updatePointFn: (tp: PathTestPoint) => void
  ) {
    //console.log(`updateTestPoint(pathTestDoc:${pathTestDoc?._id},srcTestPoint:${srcTestPoint?.id})`)
    try {
      const doc = await pathTestDoc.atomicUpdate((pathTest) => {
        let testPoint = pathTest.testPoints.find(
          (pt) => pt.id == srcTestPoint.id
        );
        if (testPoint) {
          updatePointFn(testPoint);
        } else {
          this.publishError(
            `Point ID not found in path '${pathTestDoc.pathId}'.`,
            this
          );
        }
        return pathTest;
      });
      const updatedTestPoint = doc.testPoints?.find(
        (pt) => pt.id == srcTestPoint.id
      );
      return updatedTestPoint;
    } catch (error) {
      console.error(
        `Could not update path test document ${pathTestDoc?._id} and testPoint ${srcTestPoint?.id}`
      );
      throw error;
    }
  }

  deletePathTestModal = async (
    modalService: BsModalService,
    pathTestDoc: PathTestDoc
  ) => {
    const someTestPointSeqStarted = pathTestDoc.testPoints.some((testPoint) =>
      testPoint.seqs?.some((seq) => !!seq.startTime)
    );
    const pathId = pathTestDoc.pathId;

    let initialState = null;
    let error: Error = null;

    if (someTestPointSeqStarted) {
      initialState = {
        yesBtnText: null,
        noBtnText: "OK",
        title: `Path Test In Use`,
        question:
          `<div class="m-2 small font-italic text-danger">
                    <i class='text-warning fa fa-exclamation-triangle mr-1'></i>One or more test points have a start time.</div>` +
          `<div class="pt-2">Cannot delete ${pathId} (cycle ${pathTestDoc?.cycle}).</div>`,
        focusOnNo: true,
      };
    } else {
      initialState = {
        onYes: async () => {
          try {
            await this.deletePathTest(pathTestDoc);
          } catch (e) {
            //const errorMsg = 'Delete error. ' + this.extractErrorMsg(e)
            this.publishError(e, this);
            error = e;
          }
        },
        title: `Confirm Path Test Deletion`,
        question:
          `<div class="m-2 small font-italic text-danger">
                    <i class='text-warning fa fa-exclamation-triangle mr-1'></i>Delete includes ${
                      pathTestDoc.testPoints?.length || "0"
                    } test point(s)</div>` +
          `<div class="pt-2">Delete Path Test for ${pathId} (cycle ${pathTestDoc.cycle})?</div>`,
        focusOnNo: true,
        yesStyle: "btn btn-danger",
      };
    }
    const modalRef = modalService.show(ConfirmModalComponent, {
      ignoreBackdropClick: true,
      id: 202111011,
      initialState,
    });
    await modalRef.onHidden.pipe(first()).toPromise();
    if (error) {
      initialState = {
        yesBtnText: null,
        noBtnText: "OK",
        title: `Error Deleting Path Test`,
        question: `<div class="alert alert-danger text-left">
                    <i class='fa fa-exclamation-triangle mr-2'></i>${error.message}</div>`,
        focusOnNo: true,
      };
      const modalRef = modalService.show(ConfirmModalComponent, {
        ignoreBackdropClick: true,
        id: 202111012,
        initialState,
      });
      await modalRef.onHidden.pipe(first()).toPromise();
      throw error;
    }
  };

  deletePathTest = async (pathTestDoc: PathTestDoc) => {
    console.log(
      `delete() pathTestDoc.pathId=${pathTestDoc.pathId}, pathTestDoc._id=${pathTestDoc._id}`
    );
    await pathTestDoc.remove();
    this.deleted$.emit(pathTestDoc);
  };

  export = async (doc: PathTestDoc) => {
    const withRevAndAttachments = true;
    let bWarnMissingDialerData = false;
    const pathTest = doc.toJSON(withRevAndAttachments);
    const seqs: any = [];
    for (const testPoint of pathTest?.testPoints) {
      const pointSeq: any = { testPointId: testPoint.id, testSeqs: [] };
      for (const seq of testPoint?.seqs) {
        if (seq.dialerSeqId) {
          let testSeq = (
            await this.testSeqService.rxCollection
              .findOne(seq.dialerSeqId)
              .exec()
          )?.toJSON(withRevAndAttachments) as TestSeq;
          if (!testSeq || (!testSeq.dialer && testSeq.endTime)) {
            if (this.appState.isOffline()) {
              bWarnMissingDialerData = true;
            } else {
              try {
                const serverTestSeq = await this.testSeqService.getByIdViaRest(
                  seq.dialerSeqId
                );
                if (serverTestSeq) {
                  if (!testSeq) {
                    testSeq = serverTestSeq;
                  } else {
                    testSeq.dialer = serverTestSeq.dialer;
                    if (serverTestSeq.updatedAt != testSeq.updatedAt) {
                      console.warn(
                        `Export testSeq dialer data updatedAt mismatch (local: ${testSeq.updatedAt}, server: ${serverTestSeq.updatedAt}`
                      );
                    }
                  }
                }
              } catch (error: any) {
                bWarnMissingDialerData = true;
                this.testSeqService.publishError(error, this);
              }
            }
          }
          if (testSeq) {
            pointSeq.testSeqs.push(testSeq);
          }
        }
        pointSeq.comments = (
          await this.logService.rxCollection
            .find()
            .where("taskId")
            .eq(seq.id)
            .exec()
        )?.map((doc) => doc?.toJSON(withRevAndAttachments));
      }
      seqs.push(pointSeq);
    }
    if (bWarnMissingDialerData) {
      this.appState.notifyWarning(
        `Path Test Export is missing dialer data only on server${
          this.appState.isOffline() ? " (offline)" : ""
        }`,
        this,
        15
      );
    }
    const strJson = JSON.stringify(
      {
        pathTest,
        seqs,
      },
      null,
      2
    );
    const blob = new Blob([strJson], { type: "application/json" });
    saveAs(
      blob,
      `PathTest_${pathTest.buildingId}_${pathTest.pathId}_${pathTest.stage}_${pathTest.cycle}.json`
    );
  };
}